import {
  TableCellType,
  TableHeader,
} from "../../../../../components/custom-table/table";
import { OrderAssignmentConfiguration } from "../../../../../../shared/api/services/orderAssignmentConfigService";
import { capitalizeWordsSeperatedByUnderScore } from "../../../../../utils/stringModifications";
import { MULTI_AGENT_BADGE_OPTIONS, TYPE_BADGE_OPTIONS } from "./badgeOptions";
import {
  AGENTIC_MEDICAL_ORDER_STATUSES,
  MedicalOrderStatus,
} from "./medicalOrderStatusConstants";

type Badge = (typeof MULTI_AGENT_BADGE_OPTIONS)[number];

export enum OrderAssignmentTableColumnKeys {
  UserName = "user_name",
  IfMultiRegimen = "if_multi_regimen",
  AuthStatus = "auth_status",
  PayerPatterns = "payer_patterns",
  Capacity = "capacity",
  MedicalOrderStatus = "medical_order_status",
  WorkingToday = "working_today",
  Edit = "edit",
}

export const OrderAssignmentTableHeaders: TableHeader[] = [
  {
    label: "User Name",
    key: OrderAssignmentTableColumnKeys.UserName,
    order: 1,
    width: 13,
    sortable: true,
    type: TableCellType.STRING,
  },
  {
    label: "Count",
    key: OrderAssignmentTableColumnKeys.Capacity,
    order: 2,
    width: 10,
    sortable: true,
    type: TableCellType.EDITABLE_TEXT,
  },
  {
    label: "Type",
    key: OrderAssignmentTableColumnKeys.MedicalOrderStatus,
    order: 3,
    width: 10,
    sortable: true,
    type: TableCellType.SELECTABLE_BADGE,
  },

  {
    label: "Payer",
    key: OrderAssignmentTableColumnKeys.PayerPatterns,
    order: 4,
    width: 21,
    sortable: true,
    type: TableCellType.BADGE_GROUP,
  },
  {
    label: "Auth Status",
    key: OrderAssignmentTableColumnKeys.AuthStatus,
    order: 5,
    width: 21,
    sortable: true,
    type: TableCellType.BADGE_GROUP,
  },
  {
    label: "Multi Agent",
    key: OrderAssignmentTableColumnKeys.IfMultiRegimen,
    order: 6,
    width: 10,
    sortable: true,
    type: TableCellType.SELECTABLE_BADGE,
  },
  {
    label: "Active Today",
    key: OrderAssignmentTableColumnKeys.WorkingToday,
    order: 7,
    width: 10,
    sortable: true,
    type: TableCellType.TOGGLE_SWITCH,
  },
  {
    label: "",
    key: OrderAssignmentTableColumnKeys.Edit,
    order: 8,
    width: 5,
    sortable: false,
    type: TableCellType.EDIT_ICON,
  },
];

interface GenerateOrderAssignmentTableDataOptions {
  onEdit?: (config: OrderAssignmentConfiguration) => void;
}

const formatBadges = (value: string[] | string | undefined) => {
  if (Array.isArray(value)) {
    return value.map(capitalizeWordsSeperatedByUnderScore);
  }
  return value ? [capitalizeWordsSeperatedByUnderScore(value)] : [];
};

const findBadgeWithDefault = (badges: Badge[], id: string, defaultIndex = 1) =>
  badges.find((badge) => badge.id === id) || badges[defaultIndex];

export const generateOrderAssignmentTableData = (
  configurations: OrderAssignmentConfiguration[],
  options?: GenerateOrderAssignmentTableDataOptions,
) => {
  return configurations.map((config) => {
    const editData = {
      onEdit: () => {
        options?.onEdit?.(config);
      },
    };

    // Determine the selected badge for Multi Agent
    const multiAgentValue = config.if_multi_regimen ? "yes" : "no";
    const selectedMultiAgentBadge = findBadgeWithDefault(
      MULTI_AGENT_BADGE_OPTIONS,
      multiAgentValue,
    );

    // Determine the selected badge for Medical Order Status
    // If array contains "order_completed_by_agent" or "order_in_progress", it's "agentic"
    // If array contains "yet_to_start_work_on_order", it's "human"
    // Default to "human" if not set or empty
    const medicalOrderStatus = config.medical_order_status || [];
    const isAgentic = medicalOrderStatus.some((status) =>
      AGENTIC_MEDICAL_ORDER_STATUSES.includes(status as MedicalOrderStatus),
    );
    const typeValue = isAgentic ? "agentic" : "human";
    const selectedTypeBadge = findBadgeWithDefault(
      TYPE_BADGE_OPTIONS,
      typeValue,
    );

    return {
      [OrderAssignmentTableColumnKeys.UserName]: config.provider_name ?? "",
      [OrderAssignmentTableColumnKeys.IfMultiRegimen]: {
        ...selectedMultiAgentBadge,
        badgeList: MULTI_AGENT_BADGE_OPTIONS,
        shouldUsePortal: true,
      },
      [OrderAssignmentTableColumnKeys.AuthStatus]: {
        badges: formatBadges(config.auth_status),
        emptyText: "None",
      },
      [OrderAssignmentTableColumnKeys.PayerPatterns]: {
        badges: formatBadges(config.payer_patterns),
        emptyText: "None",
      },
      [OrderAssignmentTableColumnKeys.Capacity]: {
        data: config.capacity?.toString() ?? "",
        placeholder: "Enter count...",
      },
      [OrderAssignmentTableColumnKeys.MedicalOrderStatus]: {
        ...selectedTypeBadge,
        badgeList: TYPE_BADGE_OPTIONS,
        shouldUsePortal: true,
      },
      [OrderAssignmentTableColumnKeys.WorkingToday]: {
        id: config.id ?? "",
        defaultChecked: config.working_today ?? false,
      },
      [OrderAssignmentTableColumnKeys.Edit]: editData,
      rowData: config,
    };
  });
};
